import { useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { 
  Home, 
  Users, 
  CreditCard, 
  DollarSign, 
  AlertCircle, 
  Settings, 
  FileText,
  BarChart3,
  Plug,
  ListChecks,
  Target,
  Receipt
} from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface RouteConfig {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

// Map of parent paths that don't have their own route to their default child page
const parentRedirects: Record<string, string> = {
  '/collections': '/collections/dashboard',
  '/payments': '/payments/receipts',
};

const routeConfig: Record<string, RouteConfig> = {
  'dashboard': {
    label: 'Dashboard',
    icon: Home,
  },
  'customers': {
    label: 'Customers',
    icon: Users,
  },
  'transactions': {
    label: 'Transactions',
    icon: CreditCard,
  },
  'payments': {
    label: 'Payments',
    icon: DollarSign,
  },
  'collections': {
    label: 'Collections',
    icon: ListChecks,
  },
  'queue': {
    label: 'Collections Queue',
    icon: ListChecks,
  },
  'case': {
    label: 'Case',
    icon: FileText,
  },
  'promises': {
    label: 'Promise to Pay',
    icon: ListChecks,
  },
  'strategies': {
    label: 'Strategies',
    icon: Target,
  },
  'disputes': {
    label: 'Disputes',
    icon: AlertCircle,
  },
  'credit-scoring': {
    label: 'Credit Scoring',
    icon: BarChart3,
  },
  'settings': {
    label: 'Settings',
    icon: Settings,
  },
  'users': {
    label: 'Users',
    icon: Users,
  },
  'roles': {
    label: 'Roles & Permissions',
    icon: Settings,
  },
  'templates': {
    label: 'Templates',
    icon: FileText,
  },
  'create-pdf': {
    label: 'Create Statement Template',
    icon: FileText,
  },
  'edit': {
    label: 'Edit Template',
    icon: FileText,
  },
  'integrations': {
    label: 'Integrations',
    icon: Plug,
  },
  'receipts': {
    label: 'Receipts',
    icon: Receipt,
  },
  'allocate': {
    label: 'Allocate Receipt',
    icon: DollarSign,
  },
};

interface CustomerBreadcrumbState {
  customer?: { name?: string; accountNumber?: string };
  customerName?: string;
  accountNumber?: string;
}

function getCustomerLabel(location: Location): string | null {
  const state = location.state as CustomerBreadcrumbState | null;
  if (!state) return null;
  // State passed directly from customer list: { customer: { name, accountNumber } }
  const name = state.customer?.name ?? state.customerName;
  const account = state.customer?.accountNumber ?? state.accountNumber;
  if (name && account) return `${account} - ${name}`;
  if (name) return name;
  return null;
}

function resolveDynamicSegment(
  segment: string,
  parentSegment: string | undefined,
  currentPath: string,
  location: Location,
): BreadcrumbItem | null {
  if (parentSegment === 'customers') {
    const customerLabel = getCustomerLabel(location);
    return { label: customerLabel ?? 'Customer', href: currentPath };
  }

  if (parentSegment === 'roles') {
    const roleName = segment
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return { label: roleName, href: currentPath };
  }

  const isPromiseId = parentSegment === 'promises' || /^PTP-\d+$/i.test(segment);
  const isCaseId = parentSegment === 'case' || /^[A-Z]+-\d+$/i.test(segment);
  if (isPromiseId || isCaseId) {
    return { label: segment, href: currentPath };
  }

  if (/^[0-9a-f-]+$/i.test(segment)) {
    const parentConfig = parentSegment ? routeConfig[parentSegment] : undefined;
    return { label: parentConfig?.label.slice(0, -1) || 'Detail', href: currentPath };
  }

  return null;
}

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Always start with Dashboard
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      href: '/',
      icon: Home,
    },
  ];

  // Handle special route: /analytics-reporting/multi-company/company/:companyId -> Multi-Company Dashboard > Company
  if (
    pathSegments.length >= 4 &&
    pathSegments[0] === 'analytics-reporting' &&
    pathSegments[1] === 'multi-company' &&
    pathSegments[2] === 'company'
  ) {
    breadcrumbs.push(
      { label: 'Multi-Company Dashboard', href: '/analytics-reporting/multi-company', icon: BarChart3 },
      { label: 'Company', href: `/analytics-reporting/multi-company/company/${pathSegments[3]}` },
    );
    return breadcrumbs;
  }

  // Handle special route: /settings/dispute-types/:id/workflow -> Settings > Dispute Types > Edit Dispute Type
  if (
    pathSegments.length >= 4 &&
    pathSegments[0] === 'settings' &&
    pathSegments[1] === 'dispute-types' &&
    pathSegments[3] === 'workflow'
  ) {
    breadcrumbs.push(
      { label: 'Settings', href: '/settings', icon: Settings },
      { label: 'Dispute Types', href: '/settings/dispute-types' },
      { label: 'Edit Dispute Type', href: `/settings/dispute-types/${pathSegments[2]}/workflow` },
    );
    return breadcrumbs;
  }

  // Handle special route: /payments/allocate/:receiptId -> Receipts > Allocate Receipt
  if (
    pathSegments.length >= 3 &&
    pathSegments[0] === 'payments' &&
    pathSegments[1] === 'allocate'
  ) {
    breadcrumbs.push(
      { label: 'Receipts', href: '/payments/receipts', icon: Receipt },
      { label: 'Allocate Receipt', href: `/payments/allocate/${pathSegments[2]}` },
    );
    return breadcrumbs;
  }

  // Handle special route: /settings/templates/builder/:id -> Settings > Templates > Statement Builder
  if (
    pathSegments.length >= 4 &&
    pathSegments[0] === 'settings' &&
    pathSegments[1] === 'templates' &&
    pathSegments[2] === 'builder'
  ) {
    breadcrumbs.push(
      { label: 'Settings', href: '/settings', icon: Settings },
      { label: 'Templates', href: '/settings/templates', icon: FileText },
      { label: 'Statement Builder', href: `/settings/templates/builder/${pathSegments[3]}` },
    );
    return breadcrumbs;
  }

  // Handle special route: /settings/templates/create-pdf -> Settings > Templates > Create Statement
  if (
    pathSegments.length >= 3 &&
    pathSegments[0] === 'settings' &&
    pathSegments[1] === 'templates' &&
    pathSegments[2] === 'create-pdf'
  ) {
    breadcrumbs.push(
      { label: 'Settings', href: '/settings', icon: Settings },
      { label: 'Templates', href: '/settings/templates', icon: FileText },
      { label: 'Create Statement Template', href: '/settings/templates/create-pdf', icon: FileText },
    );
    return breadcrumbs;
  }

  // Handle special route: /payments/receipts -> Dashboard > Receipts
  // Payments now has only one child (Receipts), so the intermediate 'Payments' breadcrumb is
  // unnecessary and caused duplicate React keys when href collision made reconciliation fail.
  if (
    pathSegments.length === 2 &&
    pathSegments[0] === 'payments' &&
    pathSegments[1] === 'receipts'
  ) {
    breadcrumbs.push(
      { label: 'Receipts', href: '/payments/receipts', icon: Receipt },
    );
    return breadcrumbs;
  }

  // Build breadcrumbs from path segments
  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    const config = routeConfig[segment];
    
    // Skip 'roles' segment - it will be handled by modifying the 'users' breadcrumb
    if (segment === 'roles' && pathSegments[index - 1] === 'users') {
      return;
    }

    // Skip 'case' segment when followed by a case ID — show case ID directly
    if (segment === 'case' && index < pathSegments.length - 1) {
      return;
    }
    
    if (!config) {
      const dynamicItem = resolveDynamicSegment(
        segment,
        pathSegments[index - 1],
        currentPath,
        location,
      );
      if (dynamicItem) {
        breadcrumbs.push(dynamicItem);
      } else {
        breadcrumbs.push({
          label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
          href: currentPath,
        });
      }
    } else if (segment === 'users' && pathSegments[index + 1] === 'roles') {
      breadcrumbs.push({
        label: 'Roles & Permissions',
        href: `${currentPath}?tab=roles`,
        icon: Settings,
      });
    } else {
      breadcrumbs.push({
        label: config.label,
        href: parentRedirects[currentPath] || currentPath,
        icon: config.icon,
      });
    }
  });

  return breadcrumbs;
}
